import map from 'lodash/map'
import type { ListFieldConfig } from '@ztechapps/common.hook'
import type { RichTextInputProps } from '@ztechapps/webui.input'
import { ListFieldTypeEnum } from '@ztechapps/common.hook'
import { ChatStatusEnum } from '@ztechapps/common.contract'

// The three composers render no status bar under the editor — TinyMCE's `statusbar`
// option defaults to `true`, so it is switched off here rather than left to the default.
export const composerStatusBarInit: RichTextInputProps['init'] = {
  statusbar: false,
}

// The record and inbox-thread composers' toolbar. TinyMCE's floating toolbar moves whole `|`-groups
// from the END of the string behind "…" when the header runs out of width (silver theme.js
// `partition`), so the alignment group sits last to be the first one hidden. It stays a plain
// string — an array toolbar renders no "…" button (RichTextInput.tsx:81-88).
export const composerToolbarInit: RichTextInputProps['init'] = {
  toolbar:
    'undo redo | styles | bold italic | bullist numlist outdent indent | hr | help | alignleft aligncenter alignright',
}

export const COMPOSER_DEFAULT_HEIGHT = 300
export const COMPOSER_MIN_HEIGHT = 120
export const COMPOSER_MAX_HEIGHT_RATIO = 0.7
export const COMPOSER_MESSAGE_LIST_MIN_HEIGHT = 120
// COUPLING: the thread's height outside the message list and the editor — the title row's `pb-2`
// (8px), `border-bottom` (1px) and `mb-2` (8px) (`InboxThreadLayout` in libs/chat/webui/src/Inbox/InboxThreadLayout.tsx;
// the row's content height is measured as `titleHeight`, not counted here), the error display
// wrapper's `mb-1` (4px, `InboxThreadLayout`; `ErrorDisplay` in
// libs/chat/webui/src/Chat/ErrorDisplay.tsx renders an empty `div` with no error) and the composer
// section's `border-top` (1px) and `pt-3` (16px) (`InboxComposerInput` in
// libs/chat/webui/src/Inbox/ConversationDetail.tsx); the inbox's `chatActionContainer` is
// `NullComponent` (`ConversationProvider` in libs/chat/webui/src/Conversation/Context.tsx).
// The staged-attachments row (`StagedAttachments`, `mt-2`) is not
// counted either — it shortens the message list only while files are staged.
export const COMPOSER_THREAD_CHROME_HEIGHT = 38
export const COMPOSER_KEYBOARD_STEP = 20
export const COMPOSER_HEIGHT_STORAGE_KEY = 'chat:composerHeight'
export const COMPOSER_HIDDEN_STORAGE_KEY = 'chat:composerHidden'
export const INBOX_COMPOSER_ID = 'inbox-composer'

const conversationStatuses = map(ChatStatusEnum, (status) => status)

// The requester email stays reachable through search while hidden from the default columns —
// the requester column already shows the sender's name. Remarks is hidden the same way, so a
// ticket reference stored there is searchable. The document code is a stored copy on
// the conversation, so it searches, sorts and filters like any other stored column.
// Column sizes shadow the sales orders list (libs/order/webui/src/OrderList/constants.ts)
// and the products list (libs/product/common/src/hooks/useProductListFieldConfig/index.ts):
// code/status/company/requester/tags/remarks follow the order-list equivalents, subject
// follows the product-list name column. Document and assignee have no equivalent there.
export const conversationListFieldConfig: ListFieldConfig = {
  code: { name: 'code', displayName: 'Code', index: 0, size: 100 },
  subject: { name: 'subject', displayName: 'Subject', index: 1, size: 600 },
  requester: { name: 'sender.profile.name', displayName: 'Requester', index: 2, size: 300 },
  company: { name: 'sender.company', displayName: 'Company', index: 3, size: 250 },
  status: {
    name: 'status',
    displayName: 'Status',
    type: ListFieldTypeEnum.select,
    fieldSettings: { listValues: conversationStatuses },
    excludeOnSearch: true,
    index: 4,
    size: 100,
  },
  document: {
    name: 'document.code',
    displayName: 'Document',
    index: 5,
    size: 130,
  },
  assignee: {
    name: 'assignee.profile.name',
    displayName: 'Assignee',
    type: ListFieldTypeEnum.select,
    fieldSettings: { listValues: [], emptyValueLabel: 'Unassigned' },
    excludeOnSearch: true,
    index: 6,
    size: 160,
  },
  tags: { name: 'tags', displayName: 'Tags', excludeOnSearch: true, excludeOnSort: true, index: 7, size: 120 },
  remarks: { name: 'remarks', displayName: 'Remarks', isVisible: false, size: 300 },

  lastMessageAt: {
    name: 'lastMessageAt',
    displayName: 'Last Activity',
    type: ListFieldTypeEnum.dateTimeZone,
    excludeOnSearch: true,
    size: 170,
  },
  createdAt: {
    name: 'createdAt',
    displayName: 'Created',
    type: ListFieldTypeEnum.dateTimeZone,
    excludeOnSearch: true,
    isVisible: false,
    size: 170,
  },

  requesterEmail: { name: 'sender.profile.email', displayName: 'Requester Email', isVisible: false, size: 200 },
  message: {
    name: 'searchableContent',
    displayName: 'Message',
    isVisible: false,
    excludeOnDisplay: true,
    excludeOnSort: true,
    excludeOnFilter: true,
  },
  _id: { name: '_id', isVisible: false },
}
